import React, { useState, useEffect } from 'react';
import './Leaderboard.css';
import { useNewGameContext } from '../contexts/NewGameContext';
import { calculateRank } from '../utils/rankUtils';

const LeaderboardModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { state } = useNewGameContext();
  const { players } = state;
  const [tab, setTab] = useState<'team' | 'individual'>('team');

  // 팀 순위: 팀이 있는 플레이어는 팀별 집계, 개인은 닉네임을 팀명으로 사용
  const teamRanks = Object.values(
    players.reduce((acc: Record<string, { team: string; score: number }>, p) => {
      const key = p.team || p.nickname; // 팀이 없으면 닉네임을 팀명으로 사용
      acc[key] = acc[key] || { team: key, score: 0 };
      acc[key].score += p.score;
      return acc;
    }, {})
  ).sort((a, b) => b.score - a.score);

  const individualRanks = [...players].sort((a, b) => b.score - a.score);

  // 키보드 접근성: ESC 키로 모달 닫기
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="lb-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="leaderboard-title">
      <div className="lb-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lb-header">
          <h3 id="leaderboard-title">순위</h3>
          <button 
            className="lb-close" 
            onClick={onClose}
            aria-label="순위 창 닫기"
          >×</button>
        </div>
        <div className="lb-tabs" role="tablist">
          <button 
            className={`lb-tab ${tab==='team'?'active':''}`} 
            onClick={() => setTab('team')}
            role="tab"
            id="team-tab"
            aria-selected={tab === 'team'}
            aria-controls="team-tabpanel"
          >팀 순위</button>
          <button 
            className={`lb-tab ${tab==='individual'?'active':''}`} 
            onClick={() => setTab('individual')}
            role="tab"
            id="individual-tab"
            aria-selected={tab === 'individual'}
            aria-controls="individual-tabpanel"
          >개인 순위</button>
        </div>
        <div className="lb-content">
          {tab === 'team' ? (
            <ol className="lb-list" role="tabpanel" id="team-tabpanel" aria-labelledby="team-tab">
              {teamRanks.map((t, i: number) => (
                <li key={t.team}>
                  <span className="rank" aria-label={`${calculateRank(teamRanks, i)}위`}>{calculateRank(teamRanks, i)}</span>
                  <span className="name" style={{ color: '#111' }}>{t.team}</span>
                  <span className="score" aria-label={`${t.score}점`}>{t.score}점</span>
                </li>
              ))}
            </ol>
          ) : (
            <ol className="lb-list" role="tabpanel" id="individual-tabpanel" aria-labelledby="individual-tab">
              {individualRanks.map((p, i) => (
                <li key={p.id}>
                  <span className="rank" aria-label={`${calculateRank(individualRanks, i)}위`}>{calculateRank(individualRanks, i)}</span>
                  <span className="name" style={{ color: '#111' }}>{p.nickname}{p.team ? ` (${p.team})` : ''}</span>
                  <span className="score" aria-label={`${p.score}점`}>{p.score}점</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaderboardModal;
